import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mic, MicOff, Video, VideoOff, 
  ClosedCaption, Share2, PhoneOff, 
  MoreVertical, Radio, Sparkles, MousePointer2,
  TrendingUp, ArrowUpRight, CheckCircle2,
  ArrowRight, Landmark, Smartphone, BrainCircuit, Layout
} from 'lucide-react';

/* ──────────────────────────────────────────────
   TYPES & CONFIGURATION
   ────────────────────────────────────────────── */

export type TrainingServiceType = 'general' | 'ia' | 'conciliacao' | 'smartpos';

interface ServiceConfig {
  title: string;
  badge: string;
  color: string;
  secondaryColor: string;
  icon: React.ElementType;
  instructor: {
    name: string;
    img: string;
  };
  stats: Array<{ label: string; value: string; color: string }>;
  chartLabel: string;
  chatMessages: Array<{ id: number; user: string; msg: string; time: string }>;
  insight: {
    label: string;
    text: string;
  };
}

const SERVICES_CONFIG: Record<TrainingServiceType, ServiceConfig> = {
  general: {
    title: 'Gestão Eficiente Lukos',
    badge: 'Módulo ERP',
    color: '#690093',
    secondaryColor: '#54a133',
    icon: Layout,
    instructor: {
      name: 'Jay (Expert de Sucesso)',
      img: '/assets/training/meet_session.png'
    },
    stats: [
      { label: 'Vendas Hoje', value: 'R$ 42.500', color: 'text-[#690093]' },
      { label: 'Lucro Líquido', value: 'R$ 8.120', color: 'text-[#54a133]' },
      { label: 'Estoque Médio', value: '85%', color: 'text-blue-600' }
    ],
    chartLabel: 'Rendimento Diário',
    insight: { label: 'LukIA Insight', text: 'Importação do XML concluída.' },
    chatMessages: [
      { id: 1, user: 'Alice', msg: 'Jay, quanto tempo leva para a implementação total?', time: '10:02' },
      { id: 2, user: 'Marcio', msg: 'Já vi o aumento de margem no primeiro dia!', time: '10:04' },
      { id: 3, user: 'Roberto', msg: 'O sistema já classificou os itens e otimizou o estoque!', time: '10:05' },
      { id: 4, user: 'Clara', msg: 'A conciliação automática é impecável. Recomendo!', time: '10:07' },
      { id: 5, user: 'Fernanda', msg: 'Qual o ROI médio após 3 meses de uso?', time: '10:08' },
    ]
  },
  ia: {
    title: 'Inteligência de Dados Lukos',
    badge: 'LukIA Pro',
    color: '#7c3aed',
    secondaryColor: '#db2777',
    icon: BrainCircuit,
    instructor: {
      name: 'Nina (Mentora Lukos)',
      img: '/assets/training/workshop_neutral.png'
    },
    stats: [
      { label: 'Insights Gerados', value: '1.240', color: 'text-[#7c3aed]' },
      { label: 'Acurácia Predição', value: '98.4%', color: 'text-[#db2777]' },
      { label: 'Automações Ativas', value: '12', color: 'text-indigo-600' }
    ],
    chartLabel: 'Monitor de Tendências',
    insight: { label: 'LukIA Alerta', text: 'Previsão de falta de Diesel em 48h.' },
    chatMessages: [
      { id: 1, user: 'Alice', msg: 'Nina, a IA consegue prever a falta de combustível?', time: '10:02' },
      { id: 2, user: 'Marcio', msg: 'Incrível como ela sugere o preço ideal!', time: '10:04' },
      { id: 3, user: 'Roberto', msg: 'Onde vejo os insights de margem de lucro por bico?', time: '10:05' },
      { id: 4, user: 'Clara', msg: 'Nina, me mostre os tanques que precisam de reabastecimento.', time: '10:07' },
      { id: 5, user: 'Fernanda', msg: 'Como ativo os alertas proativos no WhatsApp?', time: '10:08' },
    ]
  },
  conciliacao: {
    title: 'Auditoria e Conciliação Bancária',
    badge: 'Financeiro',
    color: '#4f46e5',
    secondaryColor: '#0ea5e9',
    icon: Landmark,
    instructor: {
      name: 'Sofia (Especialista Financeira)',
      img: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=400'
    },
    stats: [
      { label: 'Conciliado Hoje', value: 'R$ 158.400', color: 'text-[#4f46e5]' },
      { label: 'Divergências', value: '2 Encontradas', color: 'text-red-500' },
      { label: 'Taxas Médias', value: '1.85%', color: 'text-emerald-600' }
    ],
    chartLabel: 'Fluxo de Recebíveis',
    insight: { label: 'Auditoria', text: 'Taxa Stone divergente no lote #442.' },
    chatMessages: [
      { id: 1, user: 'Ricardo', msg: 'Sofia, como eu vejo as taxas de antecipação?', time: '10:10' },
      { id: 2, user: 'Julia', msg: 'A conciliação automática já bateu com o extrato da Getnet!', time: '10:12' },
      { id: 3, user: 'Beto', msg: 'Consigo importar faturas de meses anteriores?', time: '10:14' },
      { id: 4, user: 'Carla', msg: 'O sistema avisou que a Cielo não pagou o lote de terça.', time: '10:15' },
      { id: 5, user: 'Daniel', msg: 'Muito bom ver o líquido real que vai cair no banco.', time: '10:18' },
    ]
  },
  smartpos: {
    title: 'PDV Móvel e Venda de Pista',
    badge: 'Smart POS',
    color: '#f97316',
    secondaryColor: '#0ea5e9',
    icon: Smartphone,
    instructor: {
      name: 'Leo (Especialista Operacional)',
      img: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=400'
    },
    stats: [
      { label: 'Vendas Pista', value: 'R$ 12.800', color: 'text-[#f97316]' },
      { label: 'Tempo Atendimento', value: '1m 24s', color: 'text-[#0ea5e9]' },
      { label: 'Ticket Médio', value: 'R$ 184,00', color: 'text-orange-600' }
    ],
    chartLabel: 'Ocupação de Bicos',
    insight: { label: 'Operacional', text: 'Bico 04 com fluxo acima da média.' },
    chatMessages: [
      { id: 1, user: 'Fabio', msg: 'Leo, o frentista consegue fechar o turno direto na maquininha?', time: '09:45' },
      { id: 2, user: 'Sandra', msg: 'O Pix aparece confirmado na Smart POS na hora?', time: '09:48' },
      { id: 3, user: 'Tiago', msg: 'Dá pra imprimir a segunda via do cupom fiscal direto na pista?', time: '09:50' },
      { id: 4, user: 'Vitor', msg: 'Como eu ativo a integração com o bico para não precisar digitar o valor?', time: '09:53' },
      { id: 5, user: 'Mauro', msg: 'A agilidade no pagamento aumentou muito o giro aqui.', time: '09:55' },
    ]
  }
};

const participants = [
  { id: 1, name: 'Alice Silva', img: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=150' },
  { id: 2, name: 'Marcio J.', img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=150' },
];

/* ──────────────────────────────────────────────
   SUB-COMPONENTS
   ────────────────────────────────────────────── */

function SystemMockup({ config }: { config: ServiceConfig }) {
  const Icon = config.icon;
  return (
    <div className="w-full h-full bg-slate-50 p-6 flex flex-col gap-6 relative overflow-hidden font-sans">
      {/* Fake Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shadow-lg" style={{ background: config.color }}>
             <Icon size={16} className="text-white" />
          </div>
          <span className="font-black text-slate-900 text-sm tracking-tighter uppercase italic">LUKOS Dashboard</span>
        </div>
        <div className="flex gap-2">
           <div className="w-24 h-6 bg-slate-200 rounded-full animate-pulse" />
           <div className="w-8 h-6 bg-slate-200 rounded-lg animate-pulse" />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        {config.stats.map((stat, i) => (
          <motion.div 
            key={stat.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * i }}
            className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm"
          >
            <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-1">{stat.label}</p>
            <p className={`text-lg font-black ${stat.color}`}>{stat.value}</p>
          </motion.div>
        ))}
      </div>

      {/* Main Chart Area */}
      <div className="flex-1 bg-white rounded-2xl border border-slate-200 p-6 relative flex flex-col">
         <div className="flex justify-between items-center mb-6">
            <h4 className="font-black text-slate-800 text-xs uppercase tracking-widest flex items-center gap-2">
               <TrendingUp size={14} style={{ color: config.color }} /> {config.chartLabel}
            </h4>
            <span className="text-[10px] font-bold text-[#54a133] px-2 py-1 bg-[#54a133]/10 rounded-full flex items-center gap-1">
               +12.4% <ArrowUpRight size={10} />
            </span>
         </div>
         
         {/* Fake Graph Lines */}
         <div className="flex-1 flex items-end gap-2 pb-2 px-2">
            {[40, 60, 35, 75, 90, 50, 65, 80, 55, 100, 70, 85].map((h, i) => (
              <motion.div 
                key={i}
                initial={{ height: 0 }}
                animate={{ height: `${h}%` }}
                transition={{ duration: 1, delay: i * 0.05 }}
                className="flex-1 rounded-t-sm"
                style={{ background: `linear-gradient(to top, #f1f5f9, ${config.color}33)` }}
              />
            ))}
         </div>

         {/* Floating cursor animation */}
         <motion.div 
            initial={{ x: 0, y: 0 }}
            animate={{ 
               x: [0, 200, 100, 300, 0], 
               y: [0, 50, 150, 20, 0] 
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            className="absolute z-50 pointer-events-none"
         >
            <MousePointer2 size={24} style={{ color: config.color }} className="drop-shadow-lg" />
            <motion.div 
               animate={{ scale: [1, 2], opacity: [0.5, 0] }}
               transition={{ duration: 1, repeat: Infinity }}
               className="w-8 h-8 rounded-full border -translate-x-3 -translate-y-3"
               style={{ backgroundColor: `${config.color}22`, borderColor: `${config.color}44` }}
            />
         </motion.div>

         {/* Notification */}
         <motion.div 
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 2 }}
            className="absolute bottom-4 right-4 bg-slate-900 rounded-xl p-3 shadow-xl border border-white/10 flex items-center gap-3"
         >
            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `linear-gradient(135deg, ${config.secondaryColor}, ${config.color})` }}>
               <Sparkles size={16} className="text-white" />
            </div>
            <div>
               <p className="text-[10px] font-bold text-white tracking-widest uppercase">{config.insight.label}</p>
               <p className="text-[9px] text-white/60">{config.insight.text}</p>
            </div>
            <CheckCircle2 size={16} className="text-[#54a133] ml-2" />
         </motion.div>
      </div>
    </div>
  );
}

function LiveChat({ config }: { config: ServiceConfig }) {
  const [messages, setMessages] = useState(config.chatMessages.slice(0, 3));
  
  useEffect(() => {
    // Reset messages when config changes
    setMessages(config.chatMessages.slice(0, 3));
    
    const interval = setInterval(() => {
      setMessages(prev => {
        const nextIdx = (prev.length % config.chatMessages.length);
        return [...prev, config.chatMessages[nextIdx]].slice(-5);
      });
    }, 4500);
    return () => clearInterval(interval);
  }, [config]);

  return (
    <div className="h-full flex flex-col bg-[#121212]">
       <div className="p-4 border-b border-white/5 flex items-center justify-between">
          <span className="text-[10px] font-black uppercase tracking-widest text-white/50">Chat Ao Vivo</span>
          <div className="w-2 h-2 rounded-full bg-[#54a133] animate-pulse" />
       </div>
       <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide">
          <AnimatePresence initial={false}>
            {messages.map((m, i) => (
              <motion.div 
                key={`${m.id}-${i}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="group"
              >
                <div className="flex items-center justify-between mb-1">
                   <span className="text-[10px] font-black uppercase tracking-tighter" style={{ color: config.color }}>{m.user}</span>
                   <span className="text-[9px] text-white/20">{m.time}</span>
                </div>
                <div className="bg-white/5 p-3 rounded-2xl rounded-tl-none border border-white/5 group-hover:border-white/20 transition-all">
                   <p className="text-[11px] text-white/80 leading-relaxed font-bold italic">"{m.msg}"</p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
       </div>
       <div className="p-4 bg-white/3">
          <div className="flex items-center gap-2 bg-black/40 p-3 rounded-xl border border-white/10">
             <span className="text-[10px] text-white/40 flex-1">Digite sua dúvida...</span>
             <ArrowRight size={14} className="text-white/20" />
          </div>
       </div>
    </div>
  );
}

/* ──────────────────────────────────────────────
   MAIN COMPONENTE
   ────────────────────────────────────────────── */

interface TrainingLiveShowcaseProps {
  serviceType?: TrainingServiceType;
}

export default function TrainingLiveShowcase({ serviceType = 'general' }: TrainingLiveShowcaseProps) {
  const config = SERVICES_CONFIG[serviceType];
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCamOn, setIsCamOn] = useState(true);
  const [isChatOpen, setIsChatOpen] = useState(true);

  return (
    <div className="w-full max-w-6xl mx-auto rounded-[2.5rem] overflow-hidden shadow-[0_45px_100px_rgba(0,0,0,0.3)] border border-white/20 bg-[#0a0a0a] aspect-video relative group select-none flex flex-col transition-colors duration-700">
      
      {/* ─── Header Overlay ───────────────────────── */}
      <div className="absolute top-0 left-0 right-0 z-40 p-6 flex items-center justify-between pointer-events-none">
        <div className="flex items-center gap-4 bg-black/40 backdrop-blur-2xl p-2 pr-6 rounded-full border border-white/10 shadow-2xl pointer-events-auto">
          <div className="px-4 py-2 rounded-full text-[10px] font-black tracking-widest text-white shadow-xl flex items-center gap-2" style={{ background: config.color }}>
             <motion.div animate={{ opacity: [1, 0, 1] }} transition={{ repeat: Infinity, duration: 2 }} className="w-2 h-2 bg-white rounded-full" /> LIVE
          </div>
          <span className="text-white/90 text-[11px] font-black uppercase tracking-widest">{config.title}</span>
        </div>

        <div className="flex items-center gap-2 bg-black/40 backdrop-blur-2xl p-2 rounded-full border border-white/10 pointer-events-auto">
           <div className="flex -space-x-2">
              {participants.map(p => (
                <img key={p.id} src={p.img} className="w-7 h-7 rounded-full border-2 border-[#0a0a0a]" alt="" />
              ))}
              <div className="w-7 h-7 rounded-full border-2 border-[#0a0a0a] bg-[#54a133] flex items-center justify-center text-[8px] font-bold text-white">+58</div>
           </div>
           <div className="px-3 border-l border-white/10">
              <span className="text-[10px] font-black text-white/70 tracking-widest">{new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
           </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* ─── Main Shared Screen content ──────────── */}
        <div className="flex-1 relative bg-slate-100 flex items-center justify-center overflow-hidden">
           {/* Screen Share Content */}
           <div className="w-[92%] h-[85%] rounded-3xl overflow-hidden shadow-3xl border border-slate-200">
              <SystemMockup config={config} />
           </div>

           {/* Instructor PiP */}
           <motion.div 
             drag
             dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
             initial={false}
             className="absolute bottom-12 right-12 w-[180px] aspect-4/3 rounded-2xl overflow-hidden border-2 border-white/20 shadow-2xl bg-black z-30 cursor-grab active:cursor-grabbing"
           >
              <AnimatePresence mode="wait">
                <motion.img 
                  key={config.instructor.img}
                  src={config.instructor.img} 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: isCamOn ? 1 : 0.2 }}
                  exit={{ opacity: 0 }}
                  className="w-full h-full object-cover transition-opacity duration-500" 
                  alt="" 
                />
              </AnimatePresence>
              {!isCamOn && <div className="absolute inset-0 flex items-center justify-center"><VideoOff size={32} className="text-white/40" /></div>}
              <div className="absolute bottom-2 left-2 flex items-center gap-2">
                 <div className="p-1 px-2 bg-black/60 backdrop-blur-md rounded-lg flex items-center gap-2 border border-white/10">
                    <Radio size={10} style={{ color: config.color }} />
                    <span className="text-[9px] font-black text-white uppercase tracking-tighter">{config.instructor.name}</span>
                 </div>
              </div>
           </motion.div>

              <span className="text-[10px] font-black text-slate-400 tracking-[0.3em] uppercase">Sessão de Implementação</span>
        </div>

        {/* ─── Sidebar (Chat or Participants) ───────── */}
        <AnimatePresence initial={false}>
          {isChatOpen && (
            <motion.div 
               initial={{ width: 0, opacity: 0 }}
               animate={{ width: 300, opacity: 1 }}
               exit={{ width: 0, opacity: 0 }}
               className="border-l border-white/5 shadow-2xl overflow-hidden"
            >
               <LiveChat config={config} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ─── Floating Controls Area ─────────────────── */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-50 flex items-center justify-center w-full px-6 pointer-events-none">
        <motion.div 
          className="flex items-center gap-3 sm:gap-6 p-4 bg-black/60 backdrop-blur-3xl rounded-4xl border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.5)] overflow-hidden px-8 pointer-events-auto"
        >
          {/* Mic */}
          <button 
            onClick={() => setIsMicOn(!isMicOn)}
            className={`w-12 h-12 flex items-center justify-center rounded-2xl transition-all duration-300 ${isMicOn ? 'bg-white/5 hover:bg-white/10 text-white' : 'bg-red-500 text-white shadow-lg shadow-red-500/40'}`}
          >
            {isMicOn ? <Mic size={20} /> : <MicOff size={20} />}
          </button>

          {/* Cam */}
          <button 
            onClick={() => setIsCamOn(!isCamOn)}
            className={`w-12 h-12 flex items-center justify-center rounded-2xl transition-all duration-300 ${isCamOn ? 'bg-white/5 hover:bg-white/10 text-white' : 'bg-red-500 text-white shadow-lg shadow-red-500/40'}`}
          >
            {isCamOn ? <Video size={20} /> : <VideoOff size={20} />}
          </button>

          <div className="w-px h-8 bg-white/10 mx-2" />

          {/* CC */}
          <button className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/5 text-white/50">
            <ClosedCaption size={20} />
          </button>

          {/* Chat Toggle */}
          <button 
             onClick={() => setIsChatOpen(!isChatOpen)}
             className={`w-12 h-12 flex items-center justify-center rounded-2xl transition-all ${isChatOpen ? 'bg-white/10 text-white' : 'bg-white/2 text-white/30'}`}
          >
            <Share2 size={20} className={isChatOpen ? 'text-white' : 'text-white/30'} />
          </button>

          {/* Share (Mock) */}
          <button className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/5 text-white/70 hover:bg-white/10">
            <Share2 size={20} />
          </button>

          <button className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/5 text-white/70 hover:bg-white/10">
            <MoreVertical size={20} />
          </button>

          <div className="w-px h-8 bg-white/10 mx-2" />

          {/* Leave */}
          <button className="h-12 px-8 bg-red-600 hover:bg-red-700 text-white rounded-2xl flex items-center gap-3 transition-all font-black text-[11px] uppercase tracking-widest active:scale-95 shadow-lg shadow-red-900/20">
            <PhoneOff size={18} /> Encerrar
          </button>
        </motion.div>
      </div>

      {/* Floating Sparkles for Premium Look */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-10">
         <motion.div animate={{ y: [0, -100], opacity: [0, 1, 0] }} transition={{ repeat: Infinity, duration: 8 }} className="absolute bottom-0 left-1/4 w-px h-[200px]" style={{ background: `linear-gradient(to top, ${config.color}, transparent)` }} />
         <motion.div animate={{ y: [0, -150], opacity: [0, 1, 0] }} transition={{ repeat: Infinity, duration: 12, delay: 2 }} className="absolute bottom-0 right-1/4 w-px h-[100px]" style={{ background: `linear-gradient(to top, ${config.secondaryColor}, transparent)` }} />
      </div>

    </div>
  );
}
